import * as React from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { cn } from "lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { STATION_TIME_ZONE } from "../../util/stationTime";

export interface TimePickerProps {
  value?: string | null; // "HH:mm"
  onChange?: (hhmm: string | null) => void;
  minuteStep?: 5 | 10 | 15 | 20 | 30 | 60;
  start?: string; // "HH:mm"
  end?: string; // "HH:mm"
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * How to display option labels.
   * - 'HH:mm' (default): show 24h HH:mm strings regardless of locale
   * - 'locale': format using Intl.DateTimeFormat for the current language
   */
  displayFormat?: 'HH:mm' | 'locale';
  /**
   * Time zone to use when displayFormat is 'locale'. Defaults to the station's (`STATION_TIME_ZONE`).
   */
  timeZone?: string;
}

function toMinutes(hhmm: string) {
  const [h, m] = hhmm.split(":" ).map(Number);
  return h * 60 + m;
}

function toHHMM(totalMinutes: number) {
  const h = Math.floor(totalMinutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (totalMinutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

export function TimePickerSelect({
  value,
  onChange,
  minuteStep = 30,
  start = "00:00",
  end = "23:30",
  disabled,
  placeholder,
  className,
  displayFormat = 'HH:mm',
  timeZone = STATION_TIME_ZONE,
}: TimePickerProps) {
  const { t, i18n } = useTranslation();
  const ph =
    placeholder ??
    t("pages.placeholders.zeit_auswaehlen", { defaultValue: "Pick a time" });

  const startMin = toMinutes(start);
  const endMin = toMinutes(end);
  const options = React.useMemo(() => {
    const out: string[] = [];
    for (let m = startMin; m <= endMin; m += minuteStep) out.push(toHHMM(m));
    return out;
  }, [startMin, endMin, minuteStep]);

  const formatLabel = React.useCallback(
    (hhmm: string) => {
      if (displayFormat !== 'locale') return hhmm;
      const [h, m] = hhmm.split(":" ).map(Number);
      // Build an instant that represents hh:mm in the target time zone (not UTC)
      // so that formatting does not apply the zone offset twice.
      const d = moment.tz({ year: 2000, month: 0, day: 1, hour: h, minute: m }, timeZone).toDate();
      return new Intl.DateTimeFormat(i18n.language, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }).format(d);
    },
    [displayFormat, i18n.language, timeZone]
  );

  return (
    <Select
      // Keep this Select controlled for its entire lifetime. Map null/undefined to "".
      // Radix Select will show the placeholder when the value doesn't match an item.
      value={value ?? ""}
      onValueChange={(val: string) => onChange?.(val || null)}
      disabled={disabled}
    >
      <SelectTrigger className={cn(className)}>
        <SelectValue placeholder={ph} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {formatLabel(opt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default TimePickerSelect;
